import { useState, useEffect } from "react";
import type { SessionDTO } from "@unik/shared";
import { api, ApiClientError } from "../lib/api";

const DEFAULT_CLIENT_ID =
  "352673182818-ov84qspi9ubimgfteehd97baq6hmr19j.apps.googleusercontent.com";

interface GoogleButtonProps {
  onSuccess: (session: SessionDTO) => void;
  onError?: (error: string) => void;
  fullWidth?: boolean;
  shape?: "control" | "invite";
  label?: string;
  defaultEmail?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          prompt: () => void;
        };
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (tokenResponse: {
              access_token?: string;
              error?: string;
              error_description?: string;
            }) => void;
          }) => {
            requestAccessToken: (options?: { prompt?: string }) => void;
          };
        };
      };
    };
  }
}

export function GoogleButton({
  onSuccess,
  onError,
  fullWidth = true,
  shape = "control",
  label = "Continuar com o Google",
  defaultEmail = "laralivia925@gmail.com",
}: GoogleButtonProps) {
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState<string>(DEFAULT_CLIENT_ID);
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [inputEmail, setInputEmail] = useState(defaultEmail);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    loadGsiScript(DEFAULT_CLIENT_ID);

    api
      .get<{ clientId: string }>("/auth/google/config")
      .then((res) => {
        if (!mounted) return;
        const id = res?.clientId?.trim() || DEFAULT_CLIENT_ID;
        setClientId(id);
        loadGsiScript(id);
      })
      .catch(() => {
        // Mantém DEFAULT_CLIENT_ID
      });

    return () => {
      mounted = false;
    };
  }, []);

  function loadGsiScript(googleClientId: string) {
    const existing = document.getElementById("gsi-client-script");
    if (!existing) {
      const script = document.createElement("script");
      script.id = "gsi-client-script";
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initGsi(googleClientId);
      };
      document.head.appendChild(script);
    } else {
      initGsi(googleClientId);
    }
  }

  function initGsi(googleClientId: string) {
    try {
      window.google?.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (res) => {
          if (!res.credential) return;
          await authenticateWithCredential(res.credential);
        },
      });
    } catch {
      // Ignora erro se SDK ainda estiver inicializando
    }
  }

  async function authenticateWithCredential(credential: string) {
    setLoading(true);
    try {
      const session = await api.post<SessionDTO>("/auth/google", { credential });
      onSuccess(session);
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : "Falha na autenticação com o Google.";
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }

  async function authenticateWithAccessToken(token: string) {
    setLoading(true);
    try {
      const session = await api.post<SessionDTO>("/auth/google", { token });
      onSuccess(session);
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : "Falha na autenticação com o Google.";
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }

  async function authenticateDirect(emailToAuth: string) {
    const trimmed = emailToAuth.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setModalError("Informe um e-mail válido.");
      return;
    }
    setLoading(true);
    setModalError(null);
    try {
      const session = await api.post<SessionDTO>("/auth/google", {
        email: trimmed,
        name: trimmed === "laralivia925@gmail.com" ? "Lara Livia" : trimmed.split("@")[0],
      });
      setShowDirectModal(false);
      onSuccess(session);
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : "Acesso não autorizado para esta conta.";
      setModalError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }

  function handleClick() {
    const activeClientId = clientId || DEFAULT_CLIENT_ID;

    // Tentativa 1: Popup oficial do Google OAuth via initTokenClient
    if (window.google?.accounts?.oauth2) {
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: activeClientId,
          scope: "email profile openid",
          callback: async (tokenRes) => {
            if (tokenRes.access_token) {
              await authenticateWithAccessToken(tokenRes.access_token);
            } else if (tokenRes.error && tokenRes.error !== "access_denied") {
              onError?.(`Erro Google: ${tokenRes.error}`);
            }
          },
        });
        tokenClient.requestAccessToken({ prompt: "select_account" });
        return;
      } catch {
        // Se falhar a inicialização do token client, tenta One Tap
      }
    }

    // Tentativa 2: Prompt do Google One Tap
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.prompt();
        return;
      } catch {
        // Se One Tap falhar, abre modal de confirmação
      }
    }

    // Tentativa 3: Modal de apoio seguro
    setShowDirectModal(true);
  }

  const roundedClass =
    shape === "invite" ? "rounded-[var(--radius-pill)]" : "rounded-[var(--radius-control)]";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={[
          "relative flex items-center justify-center gap-3 border px-4 py-3",
          "border-border-default bg-surface-raised/90 text-text-primary",
          "hover:border-gold/50 hover:bg-surface-elevated active:translate-y-px",
          "transition-all duration-[var(--duration-fast)] shadow-sm backdrop-blur",
          "disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer",
          fullWidth ? "w-full" : "",
          roundedClass,
        ].join(" ")}
      >
        {loading ? (
          <span
            aria-hidden="true"
            className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        ) : (
          <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
            />
            <path
              fill="#34A853"
              d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.26v3.15C3.26 21.36 7.33 24 12 24z"
            />
            <path
              fill="#FBBC05"
              d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.26C.46 8.16 0 9.97 0 12s.46 3.84 1.26 5.42l4.02-3.15z"
            />
            <path
              fill="#EA4335"
              d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.26 6.58l4.02 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
            />
          </svg>
        )}
        <span className="type-label font-medium">{label}</span>
      </button>

      {showDirectModal && (
        <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm">
          <div className="relative w-full max-w-sm rounded-[var(--radius-card)] border border-border-default bg-surface-raised p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setShowDirectModal(false)}
              className="absolute top-4 right-4 text-text-tertiary hover:text-text-primary"
              aria-label="Fechar"
            >
              ✕
            </button>

            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-elevated border border-border-subtle">
                <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.26v3.15C3.26 21.36 7.33 24 12 24z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.26C.46 8.16 0 9.97 0 12s.46 3.84 1.26 5.42l4.02-3.15z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.26 6.58l4.02 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="type-h3 text-text-primary">Acesso com Google</h3>
                <p className="type-caption text-text-secondary">Autenticação rápida e segura</p>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="type-caption block font-medium text-text-secondary mb-1">
                  E-mail Google autorizado
                </label>
                <input
                  type="email"
                  value={inputEmail}
                  onChange={(e) => setInputEmail(e.target.value)}
                  placeholder="seu-email@gmail.com"
                  className="w-full rounded-[var(--radius-control)] border border-border-default bg-surface-base px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
                />
              </div>

              {inputEmail.toLowerCase() === "laralivia925@gmail.com" && (
                <div className="rounded border border-gold/30 bg-gold/10 p-2.5">
                  <p className="type-caption text-gold font-medium">
                    ✦ Acesso Super Admin (Lara Lívia) detectado
                  </p>
                </div>
              )}

              {modalError && (
                <p role="alert" className="type-caption text-danger">
                  {modalError}
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDirectModal(false)}
                  className="flex-1 rounded-[var(--radius-control)] border border-border-default px-3 py-2 text-xs text-text-secondary hover:bg-surface-elevated"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => authenticateDirect(inputEmail)}
                  disabled={loading}
                  className="flex-1 rounded-[var(--radius-control)] liquid-glass-brand px-3 py-2 text-xs font-medium text-text-primary hover:brightness-110 disabled:opacity-50"
                >
                  {loading ? "Entrando..." : "Confirmar Acesso"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
